"use client";

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { useCart } from "@/lib/cart-context";
import { formatPrice } from "@/lib/format";

type DeliveryMode = "delivery" | "pickup";
type PaymentMethod = "card" | "orange" | "wave" | "3x";

const PAYMENT_OPTIONS: { id: PaymentMethod; label: string; hint: string }[] = [
  { id: "card", label: "Carte bancaire", hint: "Visa · Mastercard · AMEX" },
  { id: "orange", label: "Orange Money", hint: "Paiement instantané" },
  { id: "wave", label: "Wave", hint: "Sans frais" },
  { id: "3x", label: "Paiement 3x sans frais", hint: "Dès 100 000 FCFA" },
];

const DELIVERY_FEE = 2500; // FCFA — flat fee, waived above 25 000

interface CheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CheckoutDialog({ open, onOpenChange }: CheckoutDialogProps) {
  const { items, totalPrice, totalCount, clear } = useCart();

  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [address, setAddress] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [delivery, setDelivery] = React.useState<DeliveryMode>("delivery");
  const [payment, setPayment] = React.useState<PaymentMethod>("card");
  const [submitting, setSubmitting] = React.useState(false);
  const [order, setOrder] = React.useState<{
    id: string;
    total: number;
    method: PaymentMethod;
    mode: DeliveryMode;
  } | null>(null);

  // Reset form when dialog closes.
  React.useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setOrder(null);
        setSubmitting(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  const deliveryFee =
    delivery === "pickup" || totalPrice >= 25000 ? 0 : DELIVERY_FEE;
  const grandTotal = totalPrice + deliveryFee;

  const canSubmit =
    name.trim().length >= 3 &&
    phone.trim().length >= 8 &&
    (delivery === "pickup" || address.trim().length >= 10) &&
    !submitting;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    // Simulate async payment processing.
    setTimeout(() => {
      const orderId = `OR-${new Date().getFullYear()}-${Math.floor(
        10000 + Math.random() * 89999
      )}`;
      setOrder({
        id: orderId,
        total: grandTotal,
        method: payment,
        mode: delivery,
      });
      setSubmitting(false);
      clear();
    }, 1400);
  }

  const paymentLabel =
    PAYMENT_OPTIONS.find((p) => p.id === order?.method)?.label ?? "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto bg-cream text-ink p-0 border-ink/10 rounded-2xl">
        <DialogHeader className="bg-ink text-cream px-6 py-5 flex flex-row items-center justify-between space-y-0">
          <div>
            <DialogTitle className="font-display text-cream text-2xl tracking-wide">
              {order ? "Commande" : "Finaliser ma"}{" "}
              <span className="gold-text italic">
                {order ? "confirmée" : "commande"}
              </span>
            </DialogTitle>
            <DialogDescription className="text-cream/55 text-[11px] tracking-[0.2em] uppercase mt-1">
              {order
                ? "Merci pour votre confiance"
                : `${totalCount} article${totalCount > 1 ? "s" : ""} · Hyper OR I`}
            </DialogDescription>
          </div>
          <DialogClose className="text-cream/60 hover:text-cream transition-colors">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </DialogClose>
        </DialogHeader>

        {order ? (
          // SUCCESS STATE
          <div className="px-6 py-8 text-center">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-gold mb-5">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5"></path>
              </svg>
            </div>
            <h3 className="font-display text-ink text-2xl mb-2">
              Votre commande est validée
            </h3>
            <p className="text-ink/55 text-sm leading-relaxed max-w-sm mx-auto">
              Un email de confirmation et le suivi SMS vous ont été envoyés.
              Notre conciergerie vous contacte sous 15 min pour finaliser la
              livraison.
            </p>

            <div className="mt-6 rounded-xl border border-ink/10 bg-parchment/40 p-4 text-left space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink/55 tracking-[0.15em] uppercase text-[11px]">
                  Numéro
                </span>
                <span className="font-display text-ink tracking-wide">
                  {order.id}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink/55 tracking-[0.15em] uppercase text-[11px]">
                  Mode
                </span>
                <span className="text-ink">
                  {order.mode === "pickup"
                    ? "Click & Collect — 30 min"
                    : "Livraison à domicile"}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink/55 tracking-[0.15em] uppercase text-[11px]">
                  Paiement
                </span>
                <span className="text-ink">{paymentLabel}</span>
              </div>
              <div className="flex items-center justify-between text-sm pt-2 border-t border-ink/10">
                <span className="text-ink/55 tracking-[0.15em] uppercase text-[11px]">
                  Total payé
                </span>
                <span className="font-display text-gold text-xl">
                  {formatPrice(order.total)}
                </span>
              </div>
            </div>

            <button
              onClick={() => onOpenChange(false)}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-ink text-cream hover:bg-gold hover:text-ink transition-colors text-[11px] tracking-[0.25em] uppercase font-semibold"
            >
              Continuer mes achats
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14"></path>
                <path d="m12 5 7 7-7 7"></path>
              </svg>
            </button>
          </div>
        ) : (
          // CHECKOUT FORM
          <form
            onSubmit={handleSubmit}
            className="px-6 py-5 space-y-5"
          >
            {/* Order summary */}
            <div className="rounded-xl border border-ink/10 bg-parchment/30 p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink/55">
                  Sous-total ({totalCount} art.)
                </span>
                <span className="text-ink">{formatPrice(totalPrice)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink/55">Livraison</span>
                <span className="text-ink">
                  {deliveryFee === 0 ? "Offerte" : formatPrice(deliveryFee)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm pt-2 border-t border-ink/10">
                <span className="font-medium tracking-[0.15em] uppercase text-[11px]">
                  Total
                </span>
                <span className="font-display text-ink text-xl">
                  {formatPrice(grandTotal)}
                </span>
              </div>
            </div>

            {/* Delivery mode */}
            <fieldset>
              <legend className="text-[11px] tracking-[0.2em] uppercase text-ink/55 mb-2">
                Mode de retrait
              </legend>
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    { id: "delivery", label: "Livraison", hint: "24-48h" },
                    { id: "pickup", label: "Click & Collect", hint: "30 min" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setDelivery(opt.id)}
                    className={`px-3 py-2.5 rounded-xl border text-left transition-all ${
                      delivery === opt.id
                        ? "border-ink bg-ink text-cream"
                        : "border-ink/15 hover:border-ink/40 text-ink"
                    }`}
                  >
                    <div className="text-sm font-medium">{opt.label}</div>
                    <div
                      className={`text-[10px] tracking-[0.15em] uppercase ${
                        delivery === opt.id ? "text-cream/60" : "text-ink/45"
                      }`}
                    >
                      {opt.hint}
                    </div>
                  </button>
                ))}
              </div>
            </fieldset>

            {/* Contact */}
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Nom complet"
                required
                value={name}
                onChange={setName}
                placeholder="Awa SENE"
              />
              <Field
                label="Téléphone"
                required
                value={phone}
                onChange={setPhone}
                placeholder="+33 6 12 34 56 78"
                type="tel"
              />
            </div>

            {delivery === "delivery" && (
              <div>
                <label className="text-[11px] tracking-[0.2em] uppercase text-ink/55">
                  Adresse de livraison <span className="text-bordeaux">*</span>
                </label>
                <textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  rows={2}
                  placeholder="12 Bd Haussmann, 75009 Paris, France"
                  className="mt-1.5 w-full rounded-xl border border-ink/15 bg-white px-3 py-2.5 text-sm text-ink placeholder:text-ink/30 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all resize-none"
                />
              </div>
            )}

            {/* Payment method */}
            <fieldset>
              <legend className="text-[11px] tracking-[0.2em] uppercase text-ink/55 mb-2">
                Paiement
              </legend>
              <div className="grid grid-cols-2 gap-2">
                {PAYMENT_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setPayment(opt.id)}
                    className={`px-3 py-2.5 rounded-xl border text-left transition-all ${
                      payment === opt.id
                        ? "border-gold bg-gold/10"
                        : "border-ink/15 hover:border-ink/40"
                    }`}
                  >
                    <div className="text-sm font-medium text-ink">
                      {opt.label}
                    </div>
                    <div className="text-[10px] tracking-[0.15em] uppercase text-ink/45">
                      {opt.hint}
                    </div>
                  </button>
                ))}
              </div>
            </fieldset>

            {/* Notes */}
            <div>
              <label className="text-[11px] tracking-[0.2em] uppercase text-ink/55">
                Note pour la conciergerie{" "}
                <span className="text-ink/30 normal-case tracking-normal">
                  (optionnel)
                </span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Cadeau : ajouter un mot manuscrit…"
                className="mt-1.5 w-full rounded-xl border border-ink/15 bg-white px-3 py-2.5 text-sm text-ink placeholder:text-ink/30 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all resize-none"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-ink text-cream hover:bg-gold hover:text-ink transition-colors text-[11px] tracking-[0.25em] uppercase font-semibold disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-ink disabled:hover:text-cream"
            >
              {submitting ? (
                <>
                  <svg
                    className="animate-spin"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                    <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                  Traitement en cours…
                </>
              ) : (
                <>
                  Confirmer & Payer {formatPrice(grandTotal)}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </>
              )}
            </button>

            <p className="text-center text-[10px] tracking-[0.15em] uppercase text-ink/40">
              Paiement sécurisé · Garantie satisfait ou remboursé 14 jours
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  type?: string;
}) {
  return (
    <div>
      <label className="text-[11px] tracking-[0.2em] uppercase text-ink/55">
        {label} {required && <span className="text-bordeaux">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-xl border border-ink/15 bg-white px-3 py-2.5 text-sm text-ink placeholder:text-ink/30 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all"
      />
    </div>
  );
}
